/** Shared primitives. The status palette and the day bar come straight from the
 *  approved mockups, so the built product matches what was signed off. */
import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'

// ---------------------------------------------------------------- status
type Kind = 'done' | 'pend' | 'rej' | 'open'

const STATUS: Record<string, { kind: Kind; label: string }> = {
  COMPLETED: { kind: 'done', label: 'Completed' },
  PENDING_APPROVAL: { kind: 'pend', label: 'Pending approval' },
  REWORK: { kind: 'rej', label: 'Rework' },
  OPEN: { kind: 'open', label: 'Open' },
  CANCELLED: { kind: 'open', label: 'Cancelled' },
}

const CHIP: Record<Kind, string> = {
  done: 'bg-done-tint text-done-ink',
  pend: 'bg-pend-tint text-pend-ink',
  rej: 'bg-rej-tint text-rej-ink',
  open: 'bg-open-tint text-open-ink',
}
const DOT: Record<Kind, string> = {
  done: 'bg-done', pend: 'bg-pend', rej: 'bg-rej', open: 'bg-open',
}
export const BAR: Record<Kind, string> = {
  done: 'bg-done', pend: 'bg-pend', rej: 'bg-rej', open: 'bg-open',
}

/** Type-to-filter, for a picker that replaced a native select.
 *
 *  Shared because two pickers had it and a third wanted it, and they must not
 *  disagree about what a query means.
 *
 *  The empty-query branch is explicit rather than load-bearing: `includes('')` is
 *  true of every string, so the filter alone would already return everything. It
 *  says "no query means no filtering" so a reader does not have to know that, and
 *  removing it changes no behaviour.
 *
 *  `trim` IS load-bearing, on the other hand - without it "  nakoda  " matches
 *  nothing at all, and trailing spaces arrive from a paste and from a stray thumb.
 *
 *  Case-insensitive and mid-string, because "sharma" should find "Sharma Textiles"
 *  and "textiles" should too - a client's distinguishing word is rarely the first.
 */
export function byName<T>(items: T[], query: string,
                          name: (x: T) => string): T[] {
  const q = query.trim().toLowerCase()
  return q ? items.filter((x) => name(x).toLowerCase().includes(q)) : items
}

/** What a status reads as, given whether an approval was involved.
 *
 *  Both facts, not a choice between them. An approved task IS completed - the
 *  approval is the thing that completes it, see the state machine's
 *  `(PENDING_APPROVAL, APPROVED) -> COMPLETED` - and this used to show only
 *  "Approved", so a task list never displayed the word people actually scan for.
 *  "Completed" goes last because it is that word; dropping either half is what the
 *  old ternary did.
 *
 *  A task completed with no approval - only rows filed before an approver became
 *  mandatory - still reads "Completed", because for those there is no second fact.
 *
 *  Pulled out of the component so the rule can be tested without a DOM, which is how
 *  everything else testable in this file is shaped.
 */
export function statusLabel(status: string, approvalStatus?: string): string {
  const s = STATUS[status] ?? { label: status }
  return status === 'COMPLETED' && approvalStatus === 'Approved'
    ? 'Approved · completed' : s.label
}

/** Status is never colour alone - the label is always present. */
export function StatusChip({ status, approvalStatus }: { status: string; approvalStatus?: string }) {
  const s = STATUS[status] ?? { kind: 'open' as Kind, label: status }
  // One chip, never two: the note below is that the status chip is the only pill on a
  // row so it means one thing, and two green pills would break it.
  const label = statusLabel(status, approvalStatus)
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${CHIP[s.kind]}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${DOT[s.kind]}`} aria-hidden />
      {label}
    </span>
  )
}

/** Priority as a file-tab edge marker, not another pill: the status chip should be
 *  the only pill on a row so it means one thing. */
export function PriorityEdge({ priority }: { priority: string }) {
  if (priority === 'Urgent') return <span className="absolute left-0 top-0.5 bottom-0.5 w-[3px] rounded-sm bg-rej" aria-label="Urgent" />
  if (priority === 'Important') return <span className="absolute left-0 top-0.5 bottom-0.5 w-[3px] rounded-sm bg-pend" aria-label="Important" />
  return null
}

// ---------------------------------------------------------------- the day bar
export interface Counters {
  total: number; completed: number; pending_approval: number
  rework: number; open: number; cancelled: number; carried_in: number
  /** Tasks running over several days that are not due yet. On the list, so inside
   *  `total`, but deliberately not in any segment - a month-long filing must not add
   *  one to thirty days' "still open". Optional because a cached `/days` response
   *  from before the field existed has no such key. */
  ongoing?: number
}

const SEGMENTS: { key: keyof Counters; kind: Kind; label: string }[] = [
  { key: 'completed', kind: 'done', label: 'Completed' },
  { key: 'pending_approval', kind: 'pend', label: 'Pending approval' },
  { key: 'rework', kind: 'rej', label: 'Rework' },
  { key: 'open', kind: 'open', label: 'Open' },
]

/** One stacked bar instead of five stat tiles: same four numbers, a quarter of the
 *  space, and the proportions readable at a glance. */
export function DayBar({ counters, title, note, onFilter, active }: {
  counters: Counters; title: string; note?: ReactNode
  onFilter?: (status: string | null) => void; active?: string | null
}) {
  const shown = SEGMENTS.filter((s) => (counters[s.key] as number) > 0)
  const total = shown.reduce((n, s) => n + (counters[s.key] as number), 0)
  return (
    <div className="card p-4 pb-3 mb-4">
      {/* Wraps, and the spacer only exists where there is room to spare. On a 390px
          phone this row is a title, a count and up to three nowrap buttons: without
          wrapping they overflowed a card that clips, so Add task - the one thing an
          owner opens Today to do - was off the right-hand edge with no way to reach
          it. The buttons take their own line rather than being dropped, because all
          three are still wanted on a phone. */}
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-2 mb-2.5">
        <b className="text-[13px] font-semibold">{title}</b>
        <span className="mono text-xs text-ink-3">
          {counters.total} tasks{counters.carried_in ? ` · ${counters.carried_in} carried in` : ''}
          {/* Said here rather than left as a silent gap, because the segments below
              deliberately do not add up to the total when there is one: these are on
              the list and not yet owed. */}
          {counters.ongoing ? ` · ${counters.ongoing} running over several days` : ''}
        </span>
        <span className="hidden sm:block flex-1" />
        {note ? <span className="w-full sm:w-auto">{note}</span> : null}
      </div>
      {total > 0 ? (
        <div className="flex h-6 gap-[2px] rounded" role="img"
             aria-label={shown.map((s) => `${s.label} ${counters[s.key]}`).join(', ')}>
          {shown.map((s, i) => (
            <button key={s.key} type="button"
              onClick={() => onFilter?.(active === s.key ? null : s.key)}
              disabled={!onFilter}
              style={{ flex: counters[s.key] as number }}
              className={`flex items-center justify-center mono text-xs font-semibold text-white
                ${BAR[s.kind]} ${i === 0 ? 'rounded-l' : ''} ${i === shown.length - 1 ? 'rounded-r' : ''}
                ${onFilter ? 'cursor-pointer hover:brightness-110' : ''}
                ${active && active !== s.key ? 'opacity-40' : ''}`}
              title={onFilter ? `Show only ${s.label.toLowerCase()}` : undefined}>
              {counters[s.key]}
            </button>
          ))}
        </div>
      ) : (
        <div className="h-6 rounded bg-rule-2 flex items-center justify-center text-xs text-ink-3">
          Nothing on this day
        </div>
      )}
      <div className="flex gap-5 mt-2.5 flex-wrap">
        {SEGMENTS.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5 text-xs text-ink-2">
            <i className={`h-2 w-2 rounded-sm ${BAR[s.kind]}`} /> {s.label} {counters[s.key]}
          </span>
        ))}
      </div>
    </div>
  )
}

/** The per-company miniature of the same bar. */
export function MiniBar({ c }: { c: Partial<Counters> }) {
  const segs = SEGMENTS.map((s) => ({ ...s, n: (c[s.key] as number) ?? 0 })).filter((s) => s.n > 0)
  if (!segs.length) return null
  return (
    <span className="inline-flex h-[7px] w-[74px] gap-[2px] rounded-sm overflow-hidden align-middle ml-2">
      {segs.map((s) => <i key={s.key} style={{ flex: s.n }} className={BAR[s.kind]} />)}
    </span>
  )
}

// ---------------------------------------------------------------- misc
export function Tag({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'bad' }) {
  return (
    <span className={`mono text-[10.5px] font-medium rounded px-1.5 py-0.5 whitespace-nowrap
      ${tone === 'bad' ? 'bg-rej-tint text-rej-ink' : 'bg-open-tint text-open-ink'}`}>
      {children}
    </span>
  )
}

export function Tabs<T extends string>({ tabs, active, onPick }: {
  /** `[value, label, badge?]`. A badge is a count or a warning marker - keep it to a
   *  character or two, because the strip has to survive eight of these on a laptop. */
  tabs: readonly (readonly [T, string, string?])[]
  active: T
  onPick: (v: T) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5 mb-3.5" role="tablist">
      {tabs.map(([v, label, badge]) => (
        <button key={v} type="button" role="tab" aria-selected={v === active}
                onClick={() => onPick(v)}
                className={`px-3 h-8 rounded-md border text-[12.5px] whitespace-nowrap ${
                  v === active
                    ? 'bg-primary text-primary-on border-primary font-medium'
                    : 'bg-surface border-rule text-ink-2 hover:text-ink'}`}>
          {label}
          {badge ? (
            <span className={`ml-1.5 mono text-[11px] ${
              v === active ? 'opacity-80' : 'text-pend-ink'}`}>{badge}</span>
          ) : null}
        </button>
      ))}
    </div>
  )
}

export function Banner({ tone, children }: { tone: 'warn' | 'info' | 'bad'; children: ReactNode }) {
  const cls = tone === 'bad' ? 'bg-rej-tint border-rej text-rej-ink'
    : tone === 'warn' ? 'bg-pend-tint border-pend text-pend-ink'
    : 'bg-primary-tint border-primary text-primary'
  return <div className={`border-l-4 rounded-r-md px-3.5 py-2.5 text-[13px] mb-4 ${cls}`}>{children}</div>
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 text-ink-3 text-[13px] py-8 justify-center">
      <span className="h-3.5 w-3.5 rounded-full border-2 border-rule border-t-primary animate-spin" />
      {label}…
    </div>
  )
}

/** An empty screen is an invitation to act, not a dead end. */
export function Empty({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="text-center py-14">
      <p className="font-semibold text-[15px]">{title}</p>
      {hint && <p className="text-ink-3 text-[13px] mt-1.5 max-w-md mx-auto">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

/** Errors explain what happened and what to do, in the interface's voice. */
export function ErrorNote({ error }: { error: unknown }) {
  const e = error as { message?: string; code?: string; requestId?: string }
  return (
    <Banner tone="bad">
      <b>{e?.message ?? 'Something went wrong.'}</b>
      {e?.requestId && <span className="mono text-[11px] opacity-70 ml-2">{e.requestId}</span>}
    </Banner>
  )
}

/** A calendar date as a person reads it, from either shape of ISO string.
 *
 *  `slice(0, 10)` is what makes it either shape. These take a date - `2026-09-08` -
 *  and appended the time themselves. The System screen's `last_seen` is a full
 *  timestamp, so that produced `2026-09-08T14:23:05T00:00:00`, which is not a date at
 *  all: the When column read "Invalid Date". Taking the date part accepts both, and a
 *  value that still will not parse falls back to the dash rather than printing
 *  "Invalid Date" at somebody.
 *
 *  Midnight is appended rather than parsing the bare date because `new Date('...')`
 *  reads a date-only string as UTC, which in IST names the previous evening - see
 *  `isoDate` below for the same trap in the other direction.
 */
const dayFrom = (iso?: string | null): Date | null => {
  if (!iso) return null
  const d = new Date(iso.slice(0, 10) + 'T00:00:00')
  return Number.isNaN(d.getTime()) ? null : d
}

export const fmtDate = (iso?: string | null) =>
  dayFrom(iso)?.toLocaleDateString('en-IN',
    { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) ?? '—'

export const fmtDay = (iso?: string | null) =>
  dayFrom(iso)?.toLocaleDateString('en-IN',
    { day: '2-digit', month: 'short' }) ?? '—'

/** The zone the firm's dates are in, as the server reports it.
 *
 *  A module value rather than context, because `isoDate` is called from inside render
 *  bodies and query keys all over the app and cannot become a hook without touching
 *  every one of them. Set once from the session payload - see `lib/auth` - and safe
 *  to read synchronously because `main.tsx` renders nothing until `me` has arrived.
 *
 *  Empty means "not told yet", which falls back to the browser's own zone: exactly
 *  the behaviour this had before, so a failure to publish it degrades rather than
 *  breaks.
 */
let firmZone = ''

export const setFirmZone = (tz: string) => { firmZone = (tz || '').trim() }
export const getFirmZone = () => firmZone

/** The date as the FIRM would write it, not as this browser would.
 *
 *  Two traps, and only one of them was handled before.
 *
 *  `toISOString()` is UTC by definition, so in IST it named *yesterday* between
 *  midnight and 05:30. Not theoretical: at 00:16 the Today screen asked the server
 *  for the previous day and drew "No tasks for today yet" underneath a header,
 *  formatted from local time, showing today's date. That is why this reads the local
 *  date parts instead.
 *
 *  But local to *whom*. Reading the browser's own parts is only right while every
 *  browser sits in the server's zone, which stopped being a safe assumption the
 *  moment the zone became a setting: a browser two hours behind the server names
 *  yesterday for the first two hours of every day, and the day it names is what gets
 *  sent back and stored as a business date.
 *
 *  So the zone comes from the server and the *instant* comes from the browser. Taking
 *  the date itself from the server would go stale in a tab left open past midnight;
 *  a zone is a fact that does not change under you.
 *
 *  Use this for anything the server will interpret as a calendar date. `toISOString`
 *  is right for an instant, and wrong for a day.
 */
export const isoDate = (d: Date = new Date()) => {
  if (firmZone) {
    try {
      // 'en-CA' formats as YYYY-MM-DD, which is the shape the API wants. Going
      // through Intl rather than arithmetic is what makes DST somebody else's
      // problem.
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: firmZone, year: 'numeric', month: '2-digit', day: '2-digit',
      }).format(d)
    } catch {
      // An unknown zone must not take the date with it. Falls through to the
      // browser's own, which is what this always did.
    }
  }
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${
    String(d.getDate()).padStart(2, '0')}`
}

export const todayISO = () => isoDate()

/** Several answers to one question, as buttons.
 *
 *  A `<select>` offers exactly one, which is a restriction the API never had: the
 *  status and priority filters have always taken a list and returned the union, so
 *  the control was the only thing insisting on one at a time. "Open or in rework" is
 *  the obvious thing to want from a task list and could not be asked.
 *
 *  Buttons rather than a multiple `<select>`: that control needs ctrl-click to pick a
 *  second value, which nobody discovers, and it shows nothing about what is currently
 *  chosen without being opened. Here the answer is visible without interacting.
 *
 *  Empty means "any", the same as it meant before, so a filter nobody has touched
 *  still filters nothing.
 */
export function Chips({ options, selected, onToggle, anyLabel = 'Any', label }: {
  options: { value: string; label: string }[]
  selected: string[]
  onToggle: (value: string) => void
  anyLabel?: string
  label?: string
}) {
  return (
    <div>
      {/* The "nothing picked" hint rides on the label line rather than sitting under
          the buttons, which is where it used to be. Below them it made this block
          TALLER than the buttons in it, so in a flex row of filters `items-center`
          centred the taller box and the chips rode high against the search field
          beside them - appearing and disappearing as the first chip was ticked.
          Callers with no label (the All tasks filter bar) now show nothing at all. */}
      {label ? (
        <label className="label">
          {label}{selected.length === 0 && anyLabel ? ` · ${anyLabel}` : ''}
        </label>
      ) : null}
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const on = selected.includes(o.value)
          return (
            <button key={o.value} type="button" aria-pressed={on}
                    onClick={() => onToggle(o.value)}
                    /* 44px on a phone, like `.btn` and `.field` - these were a flat
                       h-8 at every width, which is a 32px thumb target. */
                    className={`px-2.5 h-11 sm:h-8 rounded-md border text-[12.5px] ${
                      on ? 'bg-primary text-primary-on border-primary font-medium'
                         : 'bg-surface border-rule text-ink-2 hover:text-ink'}`}>
              {o.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

/** Add or remove one value from a selection. */
export const toggleIn = (selected: string[], value: string) =>
  selected.includes(value)
    ? selected.filter((s) => s !== value)
    : [...selected, value]


/** A stored stamp as the firm's wall clock. `—` if it will not parse.
 *
 *  Storage is UTC, so slicing a stamp is showing somebody UTC. `stamp.slice(11, 16)`
 *  read 08:53 for an alert raised at 14:23 in India, and the task history showed
 *  every event five and a half hours before it happened.
 *
 *  Goes through `Intl` in the firm's zone for the same reason `isoDate` does: DST
 *  becomes somebody else's problem, and a browser in another zone still sees the
 *  firm's time rather than its own.
 */
const inFirmZone = (iso?: string | null, opts?: Intl.DateTimeFormatOptions) => {
  if (!iso) return null
  const d = new Date(String(iso))
  if (Number.isNaN(d.getTime())) return null
  try {
    return new Intl.DateTimeFormat('en-GB', {
      ...opts, ...(firmZone ? { timeZone: firmZone } : {}),
    }).format(d)
  } catch {
    return null
  }
}

/** Just the time: `14:23`. */
export const fmtTime = (iso?: string | null) =>
  inFirmZone(iso, { hour: '2-digit', minute: '2-digit', hour12: false }) ?? '—'

/** Date and time together, for an audit trail: `08 Sep 2026, 14:23`. */
export const fmtStamp = (iso?: string | null) =>
  inFirmZone(iso, { day: '2-digit', month: 'short', year: 'numeric',
                    hour: '2-digit', minute: '2-digit', hour12: false }) ?? '—'

/** The calendar date of a stored stamp, in the firm's zone: `2026-09-08`. */
export const stampDate = (iso?: string | null) => {
  if (!iso) return '—'
  const d = new Date(String(iso))
  return Number.isNaN(d.getTime()) ? '—' : isoDate(d)
}


/** Calendar arithmetic on the FIRM's calendar, from a `YYYY-MM-DD` string.
 *
 *  These exist because doing it with a `Date` gets the answer wrong even now that
 *  `isoDate` formats in the firm's zone. `const d = new Date(); d.setDate(1)` moves
 *  to the first of the *browser's* month, and formatting that instant in the firm's
 *  zone can land on the second - a browser at 23:00 on 1 September is already the
 *  2nd in India. The month a screen opens on would be off by a day, once a month,
 *  only for people in another zone.
 *
 *  So the arithmetic happens on the date the firm is actually having. `Date.UTC` is
 *  used purely as calendar arithmetic on a date with no time in it, which is why DST
 *  cannot reach it.
 */
export const addDays = (iso: string, n: number) => {
  const [y, m, d] = iso.split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1, d))
  t.setUTCDate(t.getUTCDate() + n)
  return t.toISOString().slice(0, 10)
}

/** The first of the month the firm is currently in. */
export const monthStartISO = (iso: string = todayISO()) => `${iso.slice(0, 7)}-01`

/** The year the firm is currently in. */
export const yearNow = (iso: string = todayISO()) => Number(iso.slice(0, 4))


/** Whether the viewport is a phone, by the only breakpoint that matters here.
 *
 *  `md` and not `sm`: every table in this app has a 760px minimum, so anything
 *  narrower was scrolling sideways - which is the thing the card view exists to end.
 *
 *  A media query rather than rendering both trees and hiding one with `md:hidden`.
 *  All tasks at "all time" is the firm's whole history, and building every row twice
 *  to throw one away is a cost paid on the smallest, slowest device in the firm.
 */
export function useNarrow(query = '(max-width: 767px)') {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches)
  useEffect(() => {
    const mq = window.matchMedia(query)
    const onChange = () => setNarrow(mq.matches)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [query])
  return narrow
}
